import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { fetchUserProjects } from '../../services/dashboardService';
import { 
  FolderKanban, 
  ArrowLeft, 
  Plus,
  ChevronRight,
  Loader2,
  Search,
  Users,
  Calendar,
  CheckCircle,
  Pause,
  XCircle,
  Circle
} from 'lucide-react';

const STATUS_CONFIG = {
  active: {
    icon: Circle,
    label: 'Active',
    color: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
    dotColor: 'bg-blue-500'
  },
  completed: {
    icon: CheckCircle,
    label: 'Completed',
    color: 'bg-green-500/10 text-green-400 border-green-500/20',
    dotColor: 'bg-green-500'
  },
  on_hold: {
    icon: Pause,
    label: 'On Hold',
    color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
    dotColor: 'bg-yellow-500'
  },
  cancelled: {
    icon: XCircle,
    label: 'Cancelled',
    color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    dotColor: 'bg-zinc-500'
  }
};

const ProjectsPage = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const loadProjects = async () => {
      try {
        const data = await fetchUserProjects();
        setProjects(data.projects || []);
      } catch (err) {
        if (err.message === 'Unauthorized') {
          navigate('/login', { replace: true });
        } else {
          setError(err.message);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      loadProjects();
    }
  }, [isAuthenticated, navigate]);

  // Filter projects based on search and status
  const filteredProjects = projects.filter(project => {
    const matchesSearch = 
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.alias.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (project.description || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterStatus === 'all' || project.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  // Group by status for display
  const groupedProjects = filteredProjects.reduce((acc, project) => {
    const status = project.status || 'active';
    if (!acc[status]) acc[status] = [];
    acc[status].push(project);
    return acc;
  }, {});

  // Order: active first, then on_hold, completed, cancelled
  const statusOrder = ['active', 'on_hold', 'completed', 'cancelled'];

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 sticky top-0 bg-zinc-950/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-zinc-400 hover:text-white transition-colors">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-xl font-bold text-white">Your Projects</h1>
                <p className="text-zinc-500 text-sm">Initiatives tracked from your reflections</p>
              </div>
            </div>
            <Link
              to="/dashboard/projects/new"
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              data-testid="new-project-button"
            >
              <Plus className="w-4 h-4" />
              Add Project
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Search and Filter */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-700"
              data-testid="projects-search-input"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-3 bg-zinc-900 border border-zinc-800 rounded-lg text-white focus:outline-none focus:border-zinc-700"
            data-testid="projects-filter-select"
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>

        {/* Mobile Add Button */}
        <Link
          to="/dashboard/projects/new"
          className="sm:hidden flex items-center justify-center gap-2 w-full mb-6 px-4 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
          data-testid="new-project-button-mobile"
        >
          <Plus className="w-4 h-4" />
          Add Project
        </Link>

        {error ? (
          <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400">
            {error}
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="text-center py-16">
            <FolderKanban className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">
              {projects.length === 0 ? 'No projects tracked yet' : 'No matches found'}
            </h2>
            <p className="text-zinc-400 max-w-md mx-auto mb-6">
              {projects.length === 0 
                ? 'When you discuss work initiatives in your reflections, the AI companion will help track them.'
                : 'Try adjusting your search or filter criteria.'}
            </p>
            {projects.length === 0 && (
              <Link
                to="/dashboard/chat"
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                Start a Reflection
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-8">
            {statusOrder.map(status => {
              const statusProjects = groupedProjects[status];
              if (!statusProjects || statusProjects.length === 0) return null;
              
              const config = STATUS_CONFIG[status];
              const StatusIcon = config.icon;
              
              return (
                <div key={status}>
                  <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <StatusIcon className="w-5 h-5 text-zinc-400" />
                    {config.label}
                    <span className="text-zinc-500 font-normal">({statusProjects.length})</span>
                  </h2>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {statusProjects.map((project) => (
                      <ProjectCard key={project.project_id || project.alias} project={project} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

const ProjectCard = ({ project }) => {
  const navigate = useNavigate();
  const config = STATUS_CONFIG[project.status] || STATUS_CONFIG.active;

  const handleClick = () => {
    navigate(`/dashboard/projects/${encodeURIComponent(project.alias)}`);
  };

  const peopleCount = project.people_involved?.length || 0;

  return (
    <div
      onClick={handleClick}
      className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-700 transition-colors cursor-pointer group"
      data-testid={`project-card-${project.alias}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`w-2 h-2 rounded-full ${config.dotColor}`}></span>
            <span className="text-zinc-500 text-xs font-mono">{project.alias}</span>
          </div>
          <h3 className="text-white font-medium">{project.name}</h3>
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0 mt-1" />
      </div>
      
      {project.description && (
        <p className="mt-2 text-zinc-400 text-sm line-clamp-2">{project.description}</p>
      )}
      
      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="flex items-center gap-3">
          {peopleCount > 0 && (
            <span className="flex items-center gap-1 text-zinc-500">
              <Users className="w-3.5 h-3.5" />
              {peopleCount}
            </span>
          )}
          <span className="text-zinc-500">
            {project.mention_count || 0} mention{(project.mention_count || 0) !== 1 ? 's' : ''}
          </span>
        </div>
        {project.last_mentioned && (
          <span className="flex items-center gap-1 text-zinc-600 text-xs">
            <Calendar className="w-3 h-3" />
            {new Date(project.last_mentioned).toLocaleDateString()}
          </span>
        )}
      </div>
      
      {/* Tags */}
      {project.tags && project.tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {project.tags.slice(0, 3).map((tag, idx) => (
            <span 
              key={idx}
              className="px-2 py-0.5 bg-zinc-800 text-zinc-400 text-xs rounded"
            >
              {tag}
            </span>
          ))}
          {project.tags.length > 3 && (
            <span className="px-2 py-0.5 text-zinc-500 text-xs">
              +{project.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
